import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';

import { AuthService } from '../../auth/auth.service';
import { WarningService, WarnSeason } from '../../warning/warning.service';
import { LanguageService } from 'src/app/shared/services/language.service';

import { DatexPipe } from '../../shared/datex.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import moment from 'moment';
import { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatCardModule,
  RouterModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [DatexPipe];

@Component({
  selector: 'app-dashboard-warning',
  templateUrl: './dashboard-warning.component.html',
  styleUrls: [
    './dashboard-warning.component.scss',
    '../dashboard.component.scss',
  ],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, LoadingOverlayComponent],
})
export class DashboardWarningComponent implements OnInit {
  private injector = inject(Injector);
  private warningService = inject(WarningService);
  private auth = inject(AuthService);
  private router = inject(Router);
  public languageService = inject(LanguageService);

  // NOTE: viewChild('topCardsDashboardWarning') needed to be able to render mat-cards for top cards in dashboard view without
  // component tag which would interfere with css
  topCardsDashboardWarningTemplate = viewChild<TemplateRef<any>>(
    'topCardsDashboardWarning'
  );
  isTopCard = input<boolean>();
  egwConfig = input<any>();
  refresh = input<number>();
  // Wenn gesetzt, wird genau diese gespeicherte (angezeigte) Warnung
  // gerendert statt live über alle Regionen abgefragt (z.B. beim erneuten
  // Öffnen eines abgeschlossenen Einsatzes).
  snapshotWarning = input<any>(null);
  // Die aktuell angezeigte einzelne Warnung (egal ob live oder Snapshot) –
  // ermöglicht dem Elternteil, sie z.B. beim Speichern eines neuen
  // Einsatzprotokolls als Snapshot zu übernehmen.
  displayedWarningChange = output<any>();

  previousRefresh = signal(null);
  warningData = signal<any>(undefined);
  warningsPerType = signal({});
  areThereActiveWeatherWarnings = signal(false);
  areThereActiveWinterWarnings = signal(false);
  warnIndex = signal(0);
  latestDtg: Moment;
  isLoading = signal(true);
  egwProducts = {
    summer: [],
    winter: [],
  };

  supportedWarnTypes = ['WARN', 'VORAB'];

  // Die eine Warnung, die tatsächlich im Kartenkörper angezeigt wird –
  // fasst die Snapshot- und die Live-Auswahllogik aus dem Template zusammen.
  displayedWarning = computed(() => {
    const snapshotWarning = this.snapshotWarning();
    if (snapshotWarning) {
      return snapshotWarning;
    }
    if (this.isTopCard()) {
      return null;
    }
    const egwConfigValue = this.egwConfig();
    const warningDataValue = this.warningData();
    const warnIndexValue = this.warnIndex();
    if (
      egwConfigValue &&
      warningDataValue &&
      warningDataValue.hasOwnProperty(egwConfigValue.season) &&
      warningDataValue[egwConfigValue.season].hasOwnProperty(
        egwConfigValue.region
      ) &&
      warningDataValue[egwConfigValue.season][egwConfigValue.region].length >
        warnIndexValue
    ) {
      return warningDataValue[egwConfigValue.season][egwConfigValue.region][
        warnIndexValue
      ];
    }
    return null;
  });

  constructor() {
    this.refreshWarningsData();
    this.emitDisplayedWarningChanges();
  }

  ngOnInit(): void {
    if (this.snapshotWarning()) {
      this.isLoading.set(false);
      return;
    }
    this.initWarnRegions();
  }

  async initWarnRegions(): Promise<void> {
    const customerDocument = this.auth.getCustomerDocument();
    this.warningService.seasons.set([WarnSeason.WINTER, WarnSeason.SUMMER]);
    for (const season of this.warningService.seasons()) {
      const productType =
        season === WarnSeason.WINTER ? 'egwProducts' : 'egwSummerProducts';
      const allCustomersEgwProducts = customerDocument[productType];
      this.egwProducts[season] = allCustomersEgwProducts.filter(product =>
        this.auth.isProductActive(productType, product.id)
      );
      this.warningService.hasWarningProducts[season] =
        this.egwProducts[season].length;
    }
    this.getWarnings();
  }

  async getWarnings(): Promise<void> {
    if (this.isTopCard()) {
      await this.warningService.extractWarnRegionsSummer(
        this.egwProducts.summer
      );
      await this.warningService.extractWarnRegionsWinter(
        this.egwProducts.winter
      );

      const warningData = this.warningService.allWarningData();

      if (!this.warningData() || this.areThereNewWarnings(warningData)) {
        this.isLoading.set(true);
        this.warningData.set({ ...warningData });
        this.countWarnTypes();
      }
      this.isLoading.set(false);
    } else {
      const egwConfig = this.egwConfig();
      if (egwConfig.season === 'summer') {
        await this.warningService.extractWarnRegionsSummer(
          this.egwProducts.summer,
          egwConfig.region
        );
      } else {
        await this.warningService.extractWarnRegionsWinter(
          this.egwProducts.winter,
          egwConfig.region
        );
      }
      const warningData = this.warningService.allWarningData();
      if (!this.warningData() || this.areThereNewWarnings(warningData)) {
        this.isLoading.set(true);
        this.warningData.set({ ...warningData });
      }
      this.isLoading.set(false);
    }
  }

  areThereNewWarnings(warningData): boolean {
    let newWarnings = false;
    if (warningData) {
      for (const season of Object.keys(warningData)) {
        if (warningData[season].length > 0) {
          const dtg = moment(warningData[season]['dtg']);
          if (!this.latestDtg || this.latestDtg.isBefore(dtg)) {
            this.latestDtg = dtg;
            newWarnings = true;
          }
        }
      }
    }
    return newWarnings;
  }

  countWarnTypes(): void {
    const warningsPerRegion = {};
    for (const season of ['summer', 'winter']) {
      this.warningsPerType.update(warningsPerType => {
        return { ...warningsPerType, [season]: {} };
      });
      warningsPerRegion[season] = {};
      if (this.warningData().hasOwnProperty(season)) {
        for (const regionId of Object.keys(this.warningData()[season])) {
          warningsPerRegion[season][regionId] = [];
          if (this.warningData()[season][regionId].length > 0) {
            let entwarn = false;
            let warn = false;
            let vorab = false;
            for (const warning of this.warningData()[season][regionId]) {
              const warnType = warning['type'];
              if (warnType === 'ENTWARN' || warnType === 'AUTOENTWARN') {
                entwarn = true;
                if (!warn && warningsPerRegion[season][regionId].length === 0) {
                  warningsPerRegion[season][regionId].push(warning);
                }
                continue;
              }
              if (warnType === 'WARN' && (entwarn || warn)) {
                continue;
              }
              if (warnType === 'VORAB' && vorab) {
                continue;
              }
              if (!this.isWarningStillValid(warning)) {
                continue;
              }
              if (this.supportedWarnTypes.indexOf(warnType) >= 0) {
                if (warnType === 'VORAB') {
                  vorab = true;
                }
                if (warnType === 'WARN') {
                  warn = true;
                }
                if (season === 'summer') {
                  this.areThereActiveWeatherWarnings.set(true);
                } else {
                  this.areThereActiveWinterWarnings.set(true);
                }
                if (
                  warningsPerRegion[season][regionId].length < 2 &&
                  !entwarn
                ) {
                  warningsPerRegion[season][regionId].push(warning);
                }
                if (!this.warningsPerType()[season].hasOwnProperty(warnType)) {
                  this.warningsPerType.update(warningsPerType => {
                    return {
                      ...warningsPerType,
                      [season]: { ...warningsPerType[season], [warnType]: 1 },
                    };
                  });
                } else {
                  this.warningsPerType.update(warningsPerType => {
                    return {
                      ...warningsPerType,
                      [season]: {
                        ...warningsPerType[season],
                        [warnType]: warningsPerType[season][warnType] + 1,
                      },
                    };
                  });
                }
              }
            }
          }
        }
      }
    }
    if (!this.isTopCard()) {
      const egwConfig = this.egwConfig();
      if (warningsPerRegion.hasOwnProperty(egwConfig.season)) {
        if (
          warningsPerRegion[egwConfig.season].hasOwnProperty(egwConfig.region)
        ) {
          const warnings =
            warningsPerRegion[egwConfig.season][egwConfig.region];
          this.warnIndex.set(
            warnings.length === 1 || egwConfig.season === 'winter' ? 0 : 1
          );
        }
      }
    }
  }

  isWarningStillValid(warning: any): boolean {
    const dtg = warning.dtg;
    const runtime = warning.runtime;
    if (runtime === 0) {
      return true;
    }
    const validUntil = moment(dtg).add(runtime, 'hours');
    return moment().isSameOrBefore(validUntil);
  }

  /*
  worstWarning(): any {
    let vorab;
    const egwConfig = this.egwConfig();
    for (let i = 0; i < this.warningData()[egwConfig.season][egwConfig.region].length; i++) {
      const warning = this.warningData()[egwConfig.season][egwConfig.region][i];
      if (i == 0 && warning.TYPE === 'VORAB') {
        vorab = warning;
      } else {

      }
    }
  }
// warningData[egwConfig.season][egwConfig.region][0]


   */
  onNavigateToWarningPage(): void {
    this.router.navigate(['warning']);
  }

  private refreshWarningsData() {
    effect(
      () => {
        const refresh = this.refresh();
        untracked(() => {
          const previousRefresh = this.previousRefresh();
          if (!isNil(previousRefresh) && refresh > previousRefresh) {
            if (refresh > 1) {
              this.getWarnings();
            }
          }
          this.previousRefresh.set(refresh);
        });
      },
      { injector: this.injector }
    );
  }

  private emitDisplayedWarningChanges(): void {
    effect(
      () => {
        const warning = this.displayedWarning();
        untracked(() => this.displayedWarningChange.emit(warning ?? null));
      },
      { injector: this.injector }
    );
  }
}
